import { useState, useRef, useEffect } from 'react'
import { Upload, X, Image, AlertCircle } from 'lucide-react'

const ImageUpload = ({ 
  value, 
  onChange, 
  error, 
  label = "Image", 
  required = false,
  maxSize = 50 * 1024 * 1024, // 50MB
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/svg+xml']
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [preview, setPreview] = useState(value || '')
  const [uploadError, setUploadError] = useState('')
  const fileInputRef = useRef(null)

  // Update preview when value prop changes (e.g., when editing an existing item)
  useEffect(() => {
    // Convert regular upload URLs to proxy URLs for CORS bypass
    let processedValue = value;
    if (value && value.includes('/uploads/')) {
      const urlParts = value.split('/uploads/');
      if (urlParts.length > 1) {
        processedValue = `http://localhost:5000/image-proxy/${urlParts[1]}`;
      }
    }
    
    // Always update preview to match value prop
    setPreview(processedValue || '');
  }, [value])

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      return `File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`
    }

    // Check file type
    if (!acceptedFormats.includes(file.type)) {
      return 'Please select a valid image file (JPG, PNG, WebP, GIF, BMP, SVG)'
    }

    return null
  }

  const handleFile = (file) => {
    const validationError = validateFile(file)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    setUploadError('')
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target.result)
    }
    reader.readAsDataURL(file)

    // Pass file to parent component
    onChange(file)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const clearImage = () => {
    setPreview('')
    setUploadError('')
    onChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label} {required && <span className="text-danger-500">*</span>}
      </label>
      
      <div className="space-y-4">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
            dragActive
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : preview
              ? 'border-gray-300 dark:border-gray-600'
              : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500'
          } ${error || uploadError ? 'border-danger-500 bg-danger-50 dark:bg-danger-900/20' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          {preview ? (
            <div className="relative group">
              <img
                src={preview}
                alt="Preview"
                className="max-w-full max-h-64 mx-auto rounded-lg shadow-md object-contain"
                onError={(e) => {
                  console.error('Failed to load image:', preview);
                  setUploadError('Failed to load image. Please check the image URL or try uploading again.');
                }}
                onLoad={() => {
                  setUploadError('');
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={openFileDialog}
                    className="bg-primary-500 hover:bg-primary-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <Upload className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={clearImage}
                    className="bg-danger-500 hover:bg-danger-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-center">
                <Image className="h-12 w-12 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <button
                    type="button"
                    onClick={openFileDialog}
                    className="text-primary-500 hover:text-primary-600 font-medium"
                  >
                    Click to upload
                  </button>
                  {' or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  JPG, PNG, WebP, GIF, BMP, SVG up to {Math.round(maxSize / (1024 * 1024))}MB
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Error Messages */}
        {(error || uploadError) && (
          <div className="flex items-center space-x-2 text-danger-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error || uploadError}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageUpload